/* The tutorial's server: the scripted moves, answered in this tab.

   `wsClient.useLocalServer("game", handle)` points the real game socket at this instead of a wire
   (see `wsClient.useLocalServer`), so every control in the real game view — the city modal's
   upgrade arrows, the production panel, the route planner, END TURN — sends here and is answered
   with the same `{action, game}` messages the Django consumer sends. Nothing in the view knows.

   It is **not a simulator**, and it is important that it does not pretend to be: it applies the
   moves the script asks for and refuses everything else. A refusal goes back as the real
   server's shape — `{action: "error", message: "<Name>"}` — so the client's own
   `playerFacingError` words it, and a player who wanders off the script is told something true in
   their own language instead of watching a button do nothing.

   Where it *does* answer, it answers with the real rule: an order is charged when it stands up and
   not when it is placed, a farm harvests what its staffing allows, a city's population is capped,
   a march spends one of the round's actions. The panels around the map are the real ones, and
   every number they print is read off this board — so a rule shortened here is a panel telling a
   player something the game will not.

   The state it keeps is the game payload itself, mutated in place the way the real use cases
   mutate `Game.metadata`, plus the three things a script needs that a game does not: which step we
   are on, whether the coach's sentence for it has been read, and whether its move has been made.

   **A step starts on a clear screen** (owner, 2026-10-03: *"The tutorial should not force to close
   the dialog that explains the steps. It should force to close the other modals"*). A step that
   is over leaves sheets open — the city sheet its move was made in, the battle
   report — and the next sentence used to arrive under them, unread. So a new step opens `clear:
   false`, the hand points at the open sheet's ×, and every move of the lesson is refused as
   `TrainingCloseTheWindow` until the modal stack is empty (`markClear`, called by `views/Tutorial`,
   the one place that can see the stack). The coach's own × only folds the strip away. */

import i18n from "src/i18n"

import opening from "./opening.json"
import {
  DEFENCE_EVENTS,
  DEFENDERS_WANTED,
  HORDE_AT,
  HORDE_ID,
  LEARNER_AT,
  LEARNER_ID,
  ROAD,
  STEPS,
  actionsPerRound,
  defenceLosses,
  foundersWanted,
  harvest,
  levelOf,
  roadCost,
  soldierPrice,
  soldierUpkeep,
  trainingCapacity,
  upgradeCost,
  workerCap,
  workersPerRound,
} from "./scenario"

const clone = (value) => JSON.parse(JSON.stringify(value))

/* What goes to the store, and it is always a copy.

   Redux Toolkit freezes what it reduces, and `setGame` keeps the payload's `metadata` by
   reference — so handing the store the object this module mutates freezes it, and the next
   upgrade dies on `Cannot assign to read only property 'worker'`. The real server has this for
   free: its state lives in a database and every payload is serialized out of it. Here the clone
   is what makes that true, and it costs a few kilobytes per action. */
const payload = () => clone(state.game)

const held = (game, at, userId) => game.state.cells[at]?.metadata_by_user?.[String(userId)]
const unitNumber = (game, at, userId, unit) => held(game, at, userId)?.units?.[unit]?.number || 0

const setUnit = (game, at, userId, unit, number) => {
  const holding = held(game, at, userId)
  if (!holding) return
  holding.units[unit] = { ...(holding.units[unit] || {}), number }
}

/* The census the left dock reads. The real payload carries it computed, so every mutation has to
   leave it true — a panel reading a stale `units_per_user` is the tutorial telling a lie about
   its own board. */
const recount = (game) => {
  const census = {}
  Object.values(game.state.cells).forEach((cell) => {
    Object.entries(cell.metadata_by_user || {}).forEach(([userId, holding]) => {
      const into = (census[userId] = census[userId] || {})
      Object.entries(holding.units || {}).forEach(([unit, metadata]) => {
        into[unit] = (into[unit] || 0) + Number(metadata?.number || 0)
      })
    })
  })
  ;(game.state.movements || []).forEach((movement) => {
    const into = (census[String(movement.user_id)] = census[String(movement.user_id)] || {})
    Object.entries(movement.units || {}).forEach(([unit, metadata]) => {
      into[unit] = (into[unit] || 0) + Number(metadata?.number || 0)
    })
  })
  game.state.units_per_user = census
}

/* A march, in the shape the wire carries one. `has_arrived` is a computed field on the server's
   `Movement` and so travels with it; `position` is a plain property and does not, which is why the
   client reads `path[current_index]`. Between rounds a real march's road behind it is gone
   (`forget_the_road_behind`): `path[0]` is where it stands and `current_index` is 0, so the horde
   is handed over standing on the camp with the whole road ahead, the way the canvas would
   see it in a live game. */
const march = ({ id, userId, path, units, tick, revealedTo }) => ({
  id,
  user_id: userId,
  type: "move",
  path,
  current_index: 0,
  units,
  speed: 5,
  movement_points: 0,
  ticks_remaining: 1,
  created_tick: tick,
  ignores_water: false,
  halted: false,
  revealed_to: revealedTo,
  came_from: null,
  has_arrived: false,
})

/* ---- The state ------------------------------------------------------------------------------- */

let state = null
const listeners = new Set()
/* Which lesson owns this module. Two `views/Tutorial` can be mounted at once — the route
   remounts under `AnimatePresence`, and the outgoing one is torn down *after* the incoming one
   has started — so a `stop` has to say which run it is stopping, or the old mount's teardown
   nulls the state the new mount has just seeded and the new one boots for ever. */
let run = 0

const announce = () => listeners.forEach((listener) => listener(snapshot()))

/** What the coach reads. Null before `start` and after `stop`, which is a real window: the coach
 *  mounts with the game view, and the view is only rendered once the run exists — but a stale
 *  render on the way out must not throw. */
export const snapshot = () =>
  state && {
    game: state.game,
    step: STEPS[state.stepIndex] ?? null,
    stepIndex: state.stepIndex,
    clear: state.clear,
    acted: state.acted,
    outcome: state.outcome,
    finished: state.stepIndex >= STEPS.length,
  }

export const subscribe = (listener) => {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

/* Seeds the run. `viewerId` is who the learner's seat belongs to — the signed-in account when
   there is one, so the roster shows the player their own name and the view's `ownPlayer` lookup
   finds them. The fixture is written with `LEARNER_ID`, so re-issuing the seat is a rewrite of
   that id everywhere it appears: the seat, the cell it holds, the users block and the census.

   `username` is what the seat is called. The roster, the host star and the chat all read the
   *payload's* name and not the store's, so a signed-in player has to be written into the seat by
   name as well as by id — the fixture says "Tú", which is what an English player called Alice was
   being shown over her own city. Nobody signed in gets the locale's "You". */
export const start = (gameConfig, viewerId = LEARNER_ID, username = null) => {
  const game = clone(opening)
  if (String(viewerId) !== String(LEARNER_ID)) reissueSeat(game, viewerId)
  run += 1
  state = { game, config: gameConfig, viewerId, stepIndex: 0, clear: true, acted: false, outcome: null, run }
  const seatName = username || i18n.t("tutorial.you")
  game.players[0].user.username = seatName
  game.host = { ...game.host, username: seatName }
  game.players[1].user.username = i18n.t("tutorial.horde")
  recount(game)
  return payload()
}

const reissueSeat = (game, viewerId) => {
  const from = String(LEARNER_ID)
  const to = String(viewerId)
  game.players[0].user.id = viewerId
  game.host = { ...game.host, id: viewerId }
  game.state.users[to] = game.state.users[from]
  delete game.state.users[from]
  Object.values(game.state.cells).forEach((cell) => {
    if (String(cell.owner) === from) cell.owner = viewerId
    if (cell.metadata_by_user?.[from]) {
      cell.metadata_by_user[to] = cell.metadata_by_user[from]
      delete cell.metadata_by_user[from]
    }
    cell.discovered_by = (cell.discovered_by || []).map((id) => (String(id) === from ? viewerId : id))
  })
}

/* A generated event names the fixture's seat — in `visible_to`, in the ids its tallies are keyed by
   (`losses`, `wall_soaked`, every sub-round's sides) — so it is re-issued the way the board is.
   Only keys and the fields that hold an id: a count that happens to equal the fixture's id is a
   count. */
const ID_FIELDS = new Set(["from_user", "to_user", "by_user", "user_id"])
const ID_LISTS = new Set(["visible_to", "by_users", "victims"])
const reissueIds = (value, from, to, field = null) => {
  if (Array.isArray(value)) {
    return ID_LISTS.has(field)
      ? value.map((id) => (String(id) === from ? Number(to) : id))
      : value.map((item) => reissueIds(item, from, to))
  }
  if (value && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value).map(([key, inner]) => [key === from ? to : key, reissueIds(inner, from, to, key)])
    )
  }
  return ID_FIELDS.has(field) && String(value) === from ? Number(to) : value
}
const seatEvents = (events) =>
  String(learner()) === String(LEARNER_ID) ? clone(events) : reissueIds(events, String(LEARNER_ID), String(learner()))

/** The learner's id for this run — the fixture's, or the account the seat was re-issued to. */
const learner = () => state.viewerId
const learnerMetadata = (game) => game.state.users[String(learner())]

/** A copy of the board as it stands — for `views/Tutorial`, which re-asserts it into the
 *  store whenever something else lands there. A copy for the same reason every reply is. */
export const current = () => (state ? payload() : null)

/* ---- What a round does ------------------------------------------------------------------------
   The arithmetic that can be honest is, and in the order `process_round` runs it — the farm
   harvests, the standing army eats, the Command Center grows workers and stands the queue up —
   and only the theatre is scripted: when the horde marches, and where. The fight it marches into
   is the real engine's (`defence.json`), and the camp it leaves empty falls by the real rule. */

const raiseOrderedLevels = (game) => {
  ;(game.state.upgrades || []).forEach(({ building }) => {
    const level = levelOf(game, LEARNER_AT, learner(), building)
    const cost = upgradeCost(state.config, building, level)
    setUnit(game, LEARNER_AT, learner(), "worker", unitNumber(game, LEARNER_AT, learner(), "worker") - cost)
    held(game, LEARNER_AT, learner()).buildings[building].level = level + 1
  })
  game.state.upgrades = []
}

const foundOrderedColonies = (game) => {
  ;(game.state.foundings || []).forEach(({ coordinates }) => {
    const at = `${coordinates[0]}-${coordinates[1]}`
    const cell = game.state.cells[at]
    const mine = held(game, at, learner())
    const cost = foundersWanted(state.config, game, learner())
    const workers = unitNumber(game, at, learner(), "worker")
    if (workers - cost > 0) setUnit(game, at, learner(), "worker", workers - cost)
    else delete mine.units.worker
    cell.owner = learner()
    mine.buildings = {
      command_center: { level: 1, damage: 0, cooldown_until_tick: 0, queue: {} },
      cultivation_center: { level: 0, damage: 0, cooldown_until_tick: 0 },
      wall: { level: 0, damage: 0, cooldown_until_tick: 0 },
      academy: { level: 0, damage: 0, cooldown_until_tick: 0 },
      missile_silo: { level: 0, damage: 0, cooldown_until_tick: 0 },
    }
    const user = learnerMetadata(game)
    user.location_list = [...(user.location_list || []), [...coordinates]]
  })
  game.state.foundings = []
}

const turnTheRound = (game) => {
  const step = STEPS[state.stepIndex]
  const config = state.config
  const city = held(game, LEARNER_AT, learner())
  const soldiers = unitNumber(game, LEARNER_AT, learner(), "soldier")
  const user = learnerMetadata(game)

  // A round's events are the round's: `clear_round_events` is the first thing `process_round` does.
  game.state.events = []
  // Then what was ordered last round lands, colonies before levels (`resolve_foundings`,
  // `resolve_building_upgrades`): the lesson's orders were checked when they were given, and
  // nothing in the lesson can spend their price before the round turns.
  foundOrderedColonies(game)
  raiseOrderedLevels(game)
  const commandCentre = levelOf(game, LEARNER_AT, learner(), "command_center")

  // Collect, then eat: every city's harvest is its farm's output scaled by its staffing, and the
  // army that eats is the one standing in the city before this round's recruits stand up. A column
  // on the road prepaid its whole trip at departure (`roadCost`), which is the rule `move_troops`
  // holds. Every city of the learner's, as `collect_from_cultivation_centers` runs on every city —
  // a colony founded this round included (its farm is level 0 and yields nothing yet).
  const cities = (user.location_list || []).map(([c, r]) => `${c}-${r}`).filter((at) => held(game, at, learner()))
  const grown = cities.reduce(
    (sum, at) => sum + harvest(config, levelOf(game, at, learner(), "cultivation_center"), unitNumber(game, at, learner(), "worker")),
    0
  )
  user.food = Math.max(0, user.food + grown - soldierUpkeep(config) * soldiers)

  // Then the Command Centers: every city's population grows, to its ceiling, as
  // `build_from_command_center` grows it (a colony founded this round grows too, which is why the
  // foundings land before the economy). The ceiling stops the growth and never the people, so a
  // city over it keeps what it holds.
  cities.forEach((at) => {
    if (at === LEARNER_AT) return
    const level = levelOf(game, at, learner(), "command_center")
    const standing = unitNumber(game, at, learner(), "worker")
    setUnit(game, at, learner(), "worker", Math.max(standing, Math.min(standing + workersPerRound(config, level), workerCap(config, level))))
  })
  // The lesson's own city last: here the queue stands up out of the workers standing there —
  // charged now, as `build_from_command_center` charges it, and at most the building's capacity
  // for the round. An order that outruns the capacity carries over, which is what the production
  // panel's "N rounds" promised when it took the order.
  const workers = unitNumber(game, LEARNER_AT, learner(), "worker")
  let population = Math.max(workers, Math.min(workers + workersPerRound(config, commandCentre), workerCap(config, commandCentre)))
  const queue = city.buildings.command_center.queue || {}
  const ordered = Number(queue.soldier?.number || 0)
  const affordable = Math.floor(population / soldierPrice(config))
  const built = Math.max(0, Math.min(ordered, trainingCapacity(config, commandCentre), affordable))
  if (built > 0) {
    population -= built * soldierPrice(config)
    setUnit(game, LEARNER_AT, learner(), "soldier", soldiers + built)
  }
  setUnit(game, LEARNER_AT, learner(), "worker", population)
  const carried = ordered - built
  city.buildings.command_center.queue = carried > 0 ? { soldier: { number: carried } } : {}
  user.production_priority = carried > 0 ? [[...ROAD[0]]] : []

  state.outcome = null
  state.motion = null

  if (step === "cultivationCenter") {
    // The scenario's one piece of theatre: the horde sets out as the farm finishes, so the next
    // round opens with a march on the map — drawn by the real canvas, on the real road. Whatever
    // stands in the camp marches, so the number the city view showed there is the number on the
    // road; and it stands on the camp itself with the whole road ahead of it, the shape a march
    // has between rounds — so the show of the round after walks it from the enemy's city to the
    // gates (owner, 2026-10-23: "can we see the animation from the enemy city? So players can
    // start taking a look how it looks like"). It stood two cells out until then.
    const horde = unitNumber(game, HORDE_AT, HORDE_ID, "zombie")
    game.state.movements = [
      march({
        id: "training-horde",
        userId: HORDE_ID,
        path: ROAD.slice().reverse(),
        units: { zombie: { number: horde } },
        tick: game.tick,
        revealedTo: [learner()],
      }),
    ]
    setUnit(game, HORDE_AT, HORDE_ID, "zombie", 0)
    state.outcome = "hordeSighted"
  }

  if (step === "defend") {
    // The horde reaches the wall, and what happens there is the engine's: `defence.json` is this
    // very round played by `process_round` against the city as the lesson has built it, so the
    // losses come off the board and the report goes into the round's events — where the round
    // report and the battle view both find it, the way they find a live one.
    // And the round plays itself as a live one does (requirement 36, owner: "The tutorial does not
    // include the animations"): the horde's two cells, the contact at the gates and the clash,
    // in the engine's own shape, for the same playback the game runs.
    const horde = (game.state.movements || []).find((m) => m.id === "training-horde")
    if (horde) {
      const road = horde.path
      const moves = road.slice(1).map((to, index) => ({ kind: "move", id: horde.id, from: [...road[index]], to: [...to], pass: index }))
      const gates = [...road[road.length - 1]]
      state.motion = {
        stacks: {
          [horde.id]: {
            user_id: HORDE_ID,
            start: [...road[0]],
            units: Object.fromEntries(Object.entries(horde.units).map(([unit, metadata]) => [unit, Number(metadata?.number || 0)])),
          },
        },
        steps: [
          ...moves,
          { kind: "contact", at: gates, ids: [horde.id], pass: moves.length - 1 },
          { kind: "battle", at: gates, ended: true, winner_team: "human", teams: ["human", "zombie"] },
        ],
      }
    }
    game.state.movements = []
    Object.entries(defenceLosses()).forEach(([unit, dead]) => {
      setUnit(game, LEARNER_AT, learner(), unit, Math.max(0, unitNumber(game, LEARNER_AT, learner(), unit) - Number(dead)))
    })
    game.state.events = seatEvents(DEFENCE_EVENTS)
    state.outcome = "held"
  }

  if (step === "attack") {
    // The column lands on a camp nobody of theirs is left to hold, and the camp falls the way
    // `resolve_ownership` lets one fall: the deed goes **neutral** and its buildings are razed
    // with it. Winning ground never grants it — claiming it is `found_settlement`, which is the
    // lesson's last step. (In a live game this was the horde's last crown and it would be out of
    // the war; the rehearsal keeps it seated so the round can go on to the founding.)
    const column = (game.state.movements || []).find((m) => String(m.user_id) === String(learner()))
    const arriving = Object.fromEntries(
      Object.entries(column?.units || {}).map(([unit, metadata]) => [unit, { number: Number(metadata?.number || 0) }])
    )
    if (column) {
      const road = column.path
      const moves = road.slice(1).map((to, index) => ({ kind: "move", id: column.id, from: [...road[index]], to: [...to], pass: index }))
      const campCell = [...road[road.length - 1]]
      state.motion = {
        stacks: {
          [column.id]: {
            user_id: learner(),
            start: [...road[0]],
            units: Object.fromEntries(Object.entries(arriving).map(([unit, metadata]) => [unit, metadata.number])),
          },
        },
        // Nobody of theirs is left in the camp: the deed is taken in passing and the column lands.
        steps: [
          ...moves,
          { kind: "lost", at: campCell, from_owner: HORDE_ID, reason: "conquered" },
          { kind: "taken", at: campCell, id: column.id, from_owner: HORDE_ID },
          { kind: "land", id: column.id, at: campCell },
        ],
      }
    }
    game.state.movements = []
    const camp = game.state.cells[HORDE_AT]
    const loser = game.state.users[String(HORDE_ID)]
    camp.owner = null
    delete camp.metadata_by_user[String(HORDE_ID)]
    // `_deposit_units`: an entry with no buildings, holding exactly what marched.
    camp.metadata_by_user[String(learner())] = { buildings: {}, units: arriving }
    if (!camp.discovered_by.includes(learner())) camp.discovered_by.push(learner())
    loser.location_list = (loser.location_list || []).filter(([c, r]) => `${c}-${r}` !== HORDE_AT)
    game.state.events = [
      {
        type: "cell_lost",
        coordinates: [...ROAD[ROAD.length - 1]],
        visible_to: [learner(), HORDE_ID].sort((a, b) => Number(a) - Number(b)),
        detail: { from_user: HORDE_ID, reason: "conquered", by_users: [learner()] },
      },
    ]
    state.outcome = "campRazed"
  }

  game.tick += 1
  game.players.forEach((seat) => {
    seat.ready = seat.user.id === HORDE_ID
  })
  // Last, so the new round opens with a full budget — the same place `reset_round_actions` sits.
  Object.values(game.state.users).forEach((metadata) => {
    metadata.actions_used = 0
  })
  state.stepIndex += 1
  state.clear = false
  state.acted = false
  recount(game)
}

/* ---- The moves ---------------------------------------------------------------------------------
   Each checks what the real use case checks and refuses with the name of the real exception, so a
   refusal reaches the player as the sentence `errorMessages.js` already holds for it. */

/* The real consumer sends the class name in `message` (`{action: "error", message: "Name: ..."}`)
   and the client words it through `playerFacingError`. These names are the tutorial's own and are
   *not* backend exceptions, so their wording lives in the locale files under `errors.Training*`
   rather than in `errorMessages.js` — that map is the contract with the server, and the backend's
   `test_no_wording_is_written_for_an_exception_that_does_not_exist` guards it against exactly this
   kind of addition. `playerFacingError` reads the locale first, so they resolve either way.

   Everything else refused here is refused under a **real** exception's name, and only one the
   backend raises for the same reason: a name invented here with no wording anywhere reaches the
   player as "That action was not allowed", which is the one refusal that says nothing. */
const REFUSE = (reply, error) => reply({ action: "error", message: error })

const HANDLERS = {
  level_up_building(message, reply) {
    const game = state.game
    const at = `${message.column}-${message.row}`
    if (at !== LEARNER_AT || !held(game, at, learner())) return REFUSE(reply, "CellNotOwned")
    const wanted = STEPS[state.stepIndex] === "commandCenter" ? "command_center" : "cultivation_center"
    if (STEPS[state.stepIndex] !== "commandCenter" && STEPS[state.stepIndex] !== "cultivationCenter") {
      return REFUSE(reply, "TrainingNotThisStep")
    }
    if (message.building !== wanted) return REFUSE(reply, "TrainingNotThisStep")

    // A level is an order that lands when the round turns (owner, 2026-10-02), as
    // `level_up_building` has it: checked now, charged and raised first thing in `turnTheRound`.
    const orders = (game.state.upgrades = game.state.upgrades || [])
    if (orders.some((order) => order.building === message.building)) return REFUSE(reply, "BuildingAlreadyUpgrading")
    const level = levelOf(game, at, learner(), message.building)
    const cost = upgradeCost(state.config, message.building, level)
    if (cost > unitNumber(game, at, learner(), "worker")) return REFUSE(reply, "InsufficientWorkers")

    orders.push({ user_id: learner(), coordinates: [...ROAD[0]], building: message.building, created_tick: game.tick })
    state.acted = true
    recount(game)
    reply({ action: "user_leveled_up_building", game: payload() })
  },

  /* A level ordered this round can be taken back until it turns (owner, 2026-10-02), as
     `cancel_upgrade` has it: nothing was charged, so the order simply goes — and with it the
     step's move, which the learner then makes again. */
  cancel_upgrade(message, reply) {
    const game = state.game
    const orders = game.state.upgrades || []
    const at = `${message.column}-${message.row}`
    const index = orders.findIndex(
      (order) => order.building === message.building && `${order.coordinates[0]}-${order.coordinates[1]}` === at
    )
    if (index < 0) return REFUSE(reply, "NoUpgradeToCancel")
    orders.splice(index, 1)
    state.acted = false
    recount(game)
    reply({ action: "user_leveled_up_building", game: payload() })
  },

  /* A colony ordered this round can be taken back until it turns too (owner, 2026-10-03), as
     `cancel_founding` has it: the order goes, its action comes back (owner, 2026-10-05), and the
     step's move is made again. */
  cancel_founding(message, reply) {
    const game = state.game
    const orders = game.state.foundings || []
    const at = `${message.column}-${message.row}`
    const index = orders.findIndex((order) => `${order.coordinates[0]}-${order.coordinates[1]}` === at)
    if (index < 0) return REFUSE(reply, "NoFoundingToCancel")
    orders.splice(index, 1)
    const user = game.state.users[String(learner())]
    if (user) user.actions_used = Math.max(0, (user.actions_used || 0) - 1)
    state.acted = false
    recount(game)
    reply({ action: "user_founded_settlement", game: payload() })
  },

  /* The order *replaces* the queue and is charged when it stands up, not now — the same two rules
     `enqueue_troops` and `build_from_command_center` hold between them. An empty order clears the
     queue, which is how the production panel's "clear" works; the workers stay on the cell until
     the round turns, which is what the city view will show in a real game. No refusal for an order
     bigger than a round's capacity either, because the server has none: it builds what it can and
     carries the rest, and the panel already says how many rounds that takes. */
  enqueue_troops(message, reply) {
    const game = state.game
    const at = `${message.column}-${message.row}`
    if (at !== LEARNER_AT || !held(game, at, learner())) return REFUSE(reply, "CellNotOwned")
    if (STEPS[state.stepIndex] !== "defend") return REFUSE(reply, "TrainingNotThisStep")

    const wanted = Object.entries(message.troops || {}).filter(([, number]) => Number(number) > 0)
    if (wanted.some(([unit]) => unit !== "soldier")) return REFUSE(reply, "TrainingSoldiersOnly")

    const count = wanted.reduce((total, [, number]) => total + Math.floor(Number(number)), 0)
    // The fight this round ends in was fought by the engine against exactly this many recruits
    // (`defence.json`), so more is refused rather than trained into a report that would not add up.
    if (count > DEFENDERS_WANTED) return REFUSE(reply, "TrainingDefendersAsked")
    const centre = held(game, at, learner()).buildings.command_center
    centre.queue = count > 0 ? { soldier: { number: count } } : {}
    learnerMetadata(game).production_priority = count > 0 ? [[...ROAD[0]]] : []
    state.acted = count === DEFENDERS_WANTED
    recount(game)
    reply({ action: "user_enqueued_troops", game: payload() })
  },

  move_troops(message, reply) {
    const game = state.game
    if (STEPS[state.stepIndex] !== "attack") return REFUSE(reply, "TrainingNotThisStep")
    /* The two shapes a path comes in, and they are not the same one. The route planner builds it
       out of `{column, row}` objects (`utils/maps.expandNodesToPath`), while a *stored*
       `Movement.path` is a list of `Coordinates`, which serialize as `[column, row]` pairs. Both
       are normalised to the "col-row" key a cell is addressed by. */
    const path = (message.path || []).map((at) =>
      Array.isArray(at) ? `${at[0]}-${at[1]}` : `${at.column}-${at.row}`
    )
    // Both ends, because the map lets a player draw any route they like: the planner will happily
    // build a fourteen-cell detour into the sea that happens to start at the right city. The
    // lesson's march is the road between the two cities, and anything else is told so by name.
    if (path[0] !== LEARNER_AT || path[path.length - 1] !== HORDE_AT) return REFUSE(reply, "TrainingMarchOnTheCamp")

    /* **Whatever the player sends, marches.** The coach asks for soldiers, but the wizard's
       "todo" takes the whole city — workers included — and a server that quietly moved only the
       soldiers would leave the UI saying "618 marched" over a board still holding 546 of them.
       That is the one thing a tutorial running in the real interface must never do: the panels are
       the real ones, and they have to keep telling the truth about their own board. */
    const marching = Object.entries(message.troops || {})
      .map(([unit, number]) => [unit, Math.floor(Number(number) || 0)])
      .filter(([, number]) => number > 0)
    // The same name `move_troops` raises for "no troops selected".
    if (!marching.length) return REFUSE(reply, "InsufficientUnits")
    if (marching.some(([unit, number]) => number > unitNumber(game, LEARNER_AT, learner(), unit))) {
      return REFUSE(reply, "InsufficientUnits")
    }

    // The camp is claimed with a colony, and a human colony is paid in workers standing on the
    // ground — so the column has to carry its founders, or the last step cannot be done at all.
    const founders = marching.find(([unit]) => unit === "worker")?.[1] || 0
    if (founders < foundersWanted(state.config, game, learner())) return REFUSE(reply, "TrainingBringFounders")

    const user = learnerMetadata(game)
    const toll = roadCost(state.config, marching)
    if (toll > user.food) return REFUSE(reply, "InsufficientFoodForTrip")

    user.food -= toll
    marching.forEach(([unit, number]) => {
      setUnit(game, LEARNER_AT, learner(), unit, unitNumber(game, LEARNER_AT, learner(), unit) - number)
    })
    game.state.movements = [
      march({
        id: "training-column",
        userId: learner(),
        path: ROAD,
        units: Object.fromEntries(marching.map(([unit, number]) => [unit, { number }])),
        tick: game.tick,
        revealedTo: [],
      }),
    ]
    // A march is one of the round's actions, and the budget chip in the top bar counts it.
    user.actions_used = (user.actions_used || 0) + 1
    state.acted = true
    recount(game)
    reply({ action: "user_moved_troops", game: payload() })
  },

  /* `found_settlement`, rule for rule, on the one cell the lesson is about: an action, troops of
     yours on unowned ground, and the price in workers standing there — exactly the price taken,
     the rest staying on as the colony's first population, and the Command Center alone at level 1. */
  found_settlement(message, reply) {
    const game = state.game
    if (STEPS[state.stepIndex] !== "settle") return REFUSE(reply, "TrainingNotThisStep")
    const at = `${message.column}-${message.row}`
    if (at !== HORDE_AT) return REFUSE(reply, "TrainingSettleTheCamp")

    const user = learnerMetadata(game)
    const budget = actionsPerRound(state.config)
    if (budget && (user.actions_used || 0) >= budget) return REFUSE(reply, "NoActionsRemaining")
    const cell = game.state.cells[at]
    const mine = held(game, at, learner())
    if (!mine || !Object.values(mine.units || {}).some((unit) => Number(unit?.number) > 0)) {
      return REFUSE(reply, "NoPresenceOnCell")
    }
    if (cell.owner != null) return REFUSE(reply, "CellNotClaimable")

    const orders = (game.state.foundings = game.state.foundings || [])
    if (orders.length) return REFUSE(reply, "SettlementAlreadyOrdered")
    const cost = foundersWanted(state.config, game, learner())
    const workers = unitNumber(game, at, learner(), "worker")
    if (workers < cost) return REFUSE(reply, "InsufficientWorkers")

    // A colony is an order founded when the round turns (owner, 2026-10-02): the action is spent
    // now, the deed and the workers move in `turnTheRound`, as `resolve_foundings` moves them.
    orders.push({ user_id: learner(), coordinates: [...ROAD[ROAD.length - 1]], created_tick: game.tick })
    user.actions_used = (user.actions_used || 0) + 1
    state.acted = true
    recount(game)
    reply({ action: "user_founded_settlement", game: payload() })
  },
}

/** The battle view has been opened on the lesson's fight — `views/Tutorial` says so, off the modal
 *  stack. Reading is the battle step's whole move, and the report IS this step's clear screen:
 *  the round report's battle line is the door the coach names, so an open through it counts,
 *  with nothing to close first (the other steps wait for the sheets the last one left, `markClear`). */
export const battleOpened = () => {
  if (!state || STEPS[state.stepIndex] !== "battle" || state.acted) return
  state.clear = true
  state.acted = true
  announce()
}

/** On from a step that does not turn the round: the battle step, once the report has been read. */
export const advance = () => {
  if (!state || STEPS[state.stepIndex] !== "battle" || !state.acted) return
  state.stepIndex += 1
  state.clear = false
  state.acted = false
  state.outcome = null
  announce()
}

/** No sheet is open any more on this step: the lesson's moves are answered from here on.
 *  Idempotent, and a no-op once the lesson is over. */
export const markClear = () => {
  if (!state || state.stepIndex >= STEPS.length || state.clear) return
  state.clear = true
  announce()
}

/** END TURN is a toggle in the real UI and the host's clock does the rest; here the two are one
 *  thing, because the tutorial's only other player is a horde that is always ready. */
const endTurn = (reply) => {
  if (!state.acted) return REFUSE(reply, "TrainingFinishTheObjective")
  // Reading the report takes no round, so END TURN there is the coach's Continue.
  if (STEPS[state.stepIndex] === "battle") {
    advance()
    return reply({ action: "user_unset_ready", game: payload() })
  }
  turnTheRound(state.game)
  // The round's show rides beside the board, as `zvh/games/round_trail` sends it: the learner's
  // own side's copy, so there is nothing to redact.
  reply({ action: "game_next_round", game: payload(), motion: state.motion ? clone(state.motion) : null })
}

export const handle = (message, reply) => {
  if (!state) return
  const answer = (payload) => {
    reply(payload)
    announce()
  }

  // Un-readying changes nothing, so it is the one message answered before the screen is clear.
  if (message.action === "unset_ready") return answer({ action: "user_unset_ready", game: payload() })
  const isMove = message.action === "set_ready" || message.action === "next_round" || HANDLERS[message.action]
  if (isMove && !state.clear) return REFUSE(answer, "TrainingCloseTheWindow")

  if (message.action === "set_ready" || message.action === "next_round") return endTurn(answer)
  // Readiness is the one thing the view manages itself between rounds; un-readying (above) is a
  // no-op here, since the round turns the moment the objective is met and END TURN is pressed.

  const handler = HANDLERS[message.action]
  if (handler) return handler(message, answer)

  // Everything else the real UI can send — research, trade, a reaction, a formation — is outside
  // the lesson's moves. Refused by name rather than ignored, so the player is told why.
  answer({ action: "error", message: "TrainingNotInTraining" })
}

/** The run `start` last seeded, for `stop` to name. Null before the first `start` and after a stop. */
export const currentRun = () => state?.run ?? null

/* Ends a run. Named, so that a teardown arriving late — the outgoing route's, after the incoming
   route has already started the next lesson — leaves the newer run alone: it answers false and
   touches nothing, and the caller knows the store, the local server and the borrowed identity
   are somebody else's now. Unnamed (`stop()`) it stops whatever is running, for the tests. */
export const stop = (owner = null) => {
  if (owner != null && state?.run !== owner) return false
  state = null
  listeners.clear()
  return true
}
